import { useInitialData } from 'contexts/InitialDataContext';

interface BulkSettingsProps {
  concurrency: number;
  onConcurrencyChange: (value: number) => void;
  targetLanguages: string[];
  onTargetLanguagesChange: (languages: string[]) => void;
  sourceLanguage: string;
  disabled?: boolean;
}

export function BulkSettings({
  concurrency,
  onConcurrencyChange,
  targetLanguages,
  onTargetLanguagesChange,
  sourceLanguage,
  disabled,
}: BulkSettingsProps) {
  const { state } = useInitialData();

  const availableLanguages = (state.data?.languages ?? []).filter(
    (lang) => lang.code !== sourceLanguage && lang.code !== 'zxx' && lang.code !== 'other'
  );

  const toggleLanguage = (code: string) => {
    if (disabled) return;
    if (targetLanguages.includes(code)) {
      onTargetLanguagesChange(targetLanguages.filter((l) => l !== code));
    } else {
      onTargetLanguagesChange([...targetLanguages, code]);
    }
  };

  const selectAllLanguages = () => {
    if (disabled) return;
    onTargetLanguagesChange(availableLanguages.map((l) => l.code));
  };

  const deselectAllLanguages = () => {
    if (disabled) return;
    onTargetLanguagesChange([]);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="mb-4 text-lg font-medium text-gray-900">Step 3: Settings</h3>

      <div className="space-y-4">
        {/* Concurrency */}
        <div>
          <label htmlFor="concurrency" className="block text-sm font-medium text-gray-700">
            Concurrency (episodes in parallel)
          </label>
          <select
            id="concurrency"
            value={concurrency}
            onChange={(e) => onConcurrencyChange(parseInt(e.target.value, 10))}
            disabled={disabled}
            className="mt-1 block w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {/* Localization Languages */}
        <div>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Localization Languages
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllLanguages}
                disabled={disabled}
                className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
              >
                All
              </button>
              <button
                type="button"
                onClick={deselectAllLanguages}
                disabled={disabled}
                className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
              >
                None
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {availableLanguages.map((lang) => (
              <label
                key={lang.code}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  targetLanguages.includes(lang.code)
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-500'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={targetLanguages.includes(lang.code)}
                  onChange={() => toggleLanguage(lang.code)}
                  disabled={disabled}
                  className="sr-only"
                />
                {lang.name}
              </label>
            ))}
            {availableLanguages.length === 0 && (
              <span className="text-xs text-gray-400">No other languages available</span>
            )}
          </div>
          {targetLanguages.length === 0 && (
            <p className="mt-1 text-xs text-gray-400">Localization step will be skipped</p>
          )}
        </div>
      </div>
    </div>
  );
}
